// Lama Klub — odměnový systém (port schváleného prototypu, artifact 2026-08).
// Každých 500 km (POUZE z Commander GPS, počítá server) = booster se 2 lamami.
// Otvírání: tap na balíček → burst → server-side tah → karty rubem nahoru,
// tap = roztočení (délka dle rarity, barva se vlije během rotace) → dopad
// s efekty škálovanými raritou → souhrn. Album + žebříček přepravců.
//
// Dostupné JEN mimo směnu (vstup z Home bez směny / po směně) — během směny
// obrazovka přesměruje zpět; viz požadavek vedení.

import { useCallback, useRef, useState } from "react";
import { useHistory, Redirect } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import type { LamaCard, LamaRarity } from "@routera/shared";

import { api } from "../lib/api";
import { useShiftStore } from "../store/shiftStore";
import { LamaMark } from "../components/LamaMark";
import { ThemeToggle } from "../components/ThemeToggle";

// Statika (obrázky lamiček) žije na PWA doméně — v Capacitoru je origin jiný,
// proto absolutní base. Dev proti localhost API obrázky nemá — nevadí, alt drží.
const IMG_BASE = import.meta.env.VITE_STATIC_URL || "https://app.routera.cz";

const RAR: Record<LamaRarity, { nm: string; tok: string; glow: string }> = {
  c: { nm: "Obyčejná",   tok: "var(--lk-rc)", glow: "rgba(138,151,184,.18)" },
  r: { nm: "Vzácná",     tok: "var(--lk-rr)", glow: "rgba(91,141,239,.28)" },
  e: { nm: "Epická",     tok: "var(--lk-re)", glow: "rgba(169,112,232,.30)" },
  l: { nm: "Legendární", tok: "var(--lk-rl)", glow: "rgba(227,177,92,.38)" },
};
const SPIN_S: Record<LamaRarity, number> = { c: 1.4, r: 1.8, e: 2.2, l: 3.0 };

const isLight = () => document.documentElement.getAttribute("data-theme") === "light";
const fxColor = (r: LamaRarity) =>
  (isLight()
    ? { c: "#6E7B96", r: "#3B6CD4", e: "#8A4FC8", l: "#A87E22" }
    : { c: "#8A97B8", r: "#5B8DEF", e: "#A970E8", l: "#E3B15C" })[r];

type OpenedCard = LamaCard & { new: boolean };
type Phase = "back" | "spin" | "front";

export function LamaKlubScreen() {
  const history = useHistory();
  const shift = useShiftStore((s) => s.shift);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["mobile", "lamy"],
    queryFn: api.lamy.overview,
    staleTime: 30_000,
  });

  const [view, setView] = useState<"pack" | "album" | "lb">("pack");
  const [bursting, setBursting] = useState(false);
  const [opening, setOpening] = useState<{ cards: OpenedCard[]; idx: number; phase: Phase } | null>(null);
  const [sumCards, setSumCards] = useState<OpenedCard[] | null>(null);
  const [detail, setDetail] = useState<LamaCard | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const card3dRef = useRef<HTMLDivElement>(null);
  const raysRef = useRef<HTMLDivElement>(null);
  const ring1Ref = useRef<HTMLDivElement>(null);
  const ring2Ref = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const splashRef = useRef<HTMLDivElement>(null);
  const fxRef = useRef<HTMLCanvasElement>(null);
  const partsRef = useRef<Array<{ x: number; y: number; vx: number; vy: number; s: number; a: number; va: number; col: string; life: number }>>([]);
  const fxRunRef = useRef(false);

  // ── konfety ────────────────────────────────────────────────────────────────
  const confetti = useCallback((n: number, main: string) => {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const cv = fxRef.current, root = rootRef.current;
    if (!cv || !root) return;
    cv.width = root.clientWidth;
    cv.height = root.clientHeight;
    const cols = isLight()
      ? [main, "#C9962F", "#7E5E15", "#3B6CD4"]
      : [main, "#F3BD57", "#E8ECF5", "#5B8DEF"];
    for (let i = 0; i < n; i++) {
      partsRef.current.push({
        x: cv.width / 2, y: cv.height * 0.42,
        vx: (Math.random() - 0.5) * 11, vy: -Math.random() * 9 - 3,
        s: Math.random() * 5 + 3, a: Math.random() * Math.PI,
        va: (Math.random() - 0.5) * 0.3, col: cols[i % cols.length], life: 1,
      });
    }
    if (!fxRunRef.current) {
      fxRunRef.current = true;
      const tick = () => {
        const ctx = cv.getContext("2d");
        if (!ctx) { fxRunRef.current = false; return; }
        ctx.clearRect(0, 0, cv.width, cv.height);
        partsRef.current = partsRef.current.filter((p) => p.life > 0);
        for (const p of partsRef.current) {
          p.x += p.vx; p.y += p.vy; p.vy += 0.28; p.a += p.va; p.life -= 0.008;
          ctx.save();
          ctx.translate(p.x, p.y); ctx.rotate(p.a);
          ctx.globalAlpha = Math.max(0, p.life);
          ctx.fillStyle = p.col;
          ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6);
          ctx.restore();
        }
        if (partsRef.current.length) requestAnimationFrame(tick);
        else { fxRunRef.current = false; ctx.clearRect(0, 0, cv.width, cv.height); }
      };
      requestAnimationFrame(tick);
    }
  }, []);

  const retrig = (el: HTMLElement | null) => {
    if (!el) return;
    el.classList.remove("go");
    void el.offsetWidth;
    el.classList.add("go");
  };

  const fxFor = useCallback((r: LamaRarity) => {
    if (r === "c") return;
    const col = fxColor(r);
    revealRef.current?.style.setProperty("--lk-fxc", col);
    retrig(flashRef.current);
    retrig(ring1Ref.current);
    if (r !== "r") {
      raysRef.current?.style.setProperty("--lk-rayc", isLight()
        ? (r === "e" ? "rgba(138,79,200,.20)" : "rgba(168,126,34,.22)")
        : (r === "e" ? "rgba(169,112,232,.16)" : "rgba(227,177,92,.15)"));
      raysRef.current?.classList.add("on");
      retrig(ring2Ref.current);
    }
    if (r === "r") confetti(40, col);
    if (r === "e") {
      confetti(90, col);
      if (splashRef.current) splashRef.current.textContent = "EPICKÁ!";
      retrig(splashRef.current);
    }
    if (r === "l") {
      const rev = revealRef.current;
      if (rev) { rev.classList.remove("shake"); void rev.offsetWidth; rev.classList.add("shake"); }
      confetti(170, col);
      setTimeout(() => confetti(90, isLight() ? "#8A6A10" : "#F3D9A4"), 450);
      setTimeout(() => confetti(60, isLight() ? "#B08414" : "#F3BD57"), 900);
      if (splashRef.current) splashRef.current.textContent = "LEGENDÁRNÍ!";
      retrig(splashRef.current);
    }
  }, [confetti]);

  // ── otevření balíčku ──────────────────────────────────────────────────────
  const startOpen = async () => {
    if (bursting || opening || !data || data.packs_available <= 0) return;
    setBursting(true);
    try {
      const res = await api.lamy.open();
      qc.setQueryData(["mobile", "lamy"], (old: typeof data | undefined) =>
        old ? { ...old, packs_available: res.packs_available, km_total: res.km_total, km_to_next: res.km_to_next } : old);
      setTimeout(() => {
        setBursting(false);
        setOpening({ cards: res.cards, idx: 0, phase: "back" });
      }, 560);
    } catch {
      setBursting(false);
      void qc.invalidateQueries({ queryKey: ["mobile", "lamy"] });
    }
  };

  const resetCardFx = () => {
    raysRef.current?.classList.remove("on");
    splashRef.current?.classList.remove("go");
    flashRef.current?.classList.remove("go");
    ring1Ref.current?.classList.remove("go");
    ring2Ref.current?.classList.remove("go");
    wrapRef.current?.classList.remove("sg-r", "sg-e", "sg-l");
  };

  const revealTap = () => {
    if (!opening) return;
    const c = opening.cards[opening.idx];
    if (opening.phase === "back") {
      const el = card3dRef.current;
      if (!el) return;
      if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setOpening({ ...opening, phase: "front" });
        fxFor(c.rarity);
        return;
      }
      el.style.setProperty("--lk-sd", `${SPIN_S[c.rarity]}s`);
      setOpening({ ...opening, phase: "spin" });
      // barva rarity se vlije až v průběhu rotace — žádný spoiler předem
      if (c.rarity !== "c") {
        setTimeout(() => wrapRef.current?.classList.add(`sg-${c.rarity}`), SPIN_S[c.rarity] * 520);
      }
    } else if (opening.phase === "front") {
      const el = card3dRef.current;
      el?.classList.add("away");
      setTimeout(() => {
        el?.classList.remove("away");
        resetCardFx();
        if (opening.idx < opening.cards.length - 1) {
          setOpening({ cards: opening.cards, idx: opening.idx + 1, phase: "back" });
        } else {
          setOpening(null);
          setSumCards(opening.cards);
          void qc.invalidateQueries({ queryKey: ["mobile", "lamy"] });
        }
      }, 300);
    }
  };

  // Dopad rotace: 1980° ≡ 180°, takže výměna animace za statický flip není
  // vidět — ale MUSÍ proběhnout synchronně v DOMu (React state update by mezi
  // odebráním .spin a přidáním .flip nechal proběhnout přechod 0°→180°).
  const onSpinEnd = () => {
    if (!opening || opening.phase !== "spin") return;
    const c = opening.cards[opening.idx];
    const el = card3dRef.current;
    if (el) {
      el.style.transition = "none";
      el.classList.remove("spin");
      el.classList.add("flip");
      void el.offsetWidth;
      el.style.transition = "";
    }
    setOpening({ ...opening, phase: "front" });
    fxFor(c.rarity);
  };

  // ── guardy: během směny sem řidič nemá lézt; vypnutý flag = pryč ─────────
  if (shift && !shift.end_ts) return <Redirect to="/" />;
  if (data && !data.enabled) return <Redirect to="/" />;

  const set = data?.set ?? [];
  const inv = data?.inventar ?? {};
  const owned = set.filter((c) => inv[String(c.id)]).length;
  const cur = opening?.cards[opening.idx];
  const kmProg = data ? ((data.km_total % data.km_per_pack) / data.km_per_pack) * 100 : 0;

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div ref={rootRef} className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.replace("/")}>‹</button>
            <div style={{ flex: 1 }}>
              <b style={{ fontSize: 17, letterSpacing: ".06em" }}>LAMA KLUB</b>
            </div>
            <ThemeToggle />
          </div>

          {/* km + balíčky */}
          <div className="lk-km">
            <div className="lk-kmrow">
              <b>{(data?.km_total ?? 0).toLocaleString("cs-CZ")} <small>km najeto</small></b>
              <span className="lk-pill">
                {data == null ? "…" : data.packs_available === 1 ? "1 balíček"
                  : data.packs_available >= 2 && data.packs_available <= 4
                    ? `${data.packs_available} balíčky` : `${data.packs_available} balíčků`}
              </span>
            </div>
            <div className="lk-bar"><i style={{ width: `${kmProg}%` }} /></div>
            <div className="lk-barlbl">
              <span>{data ? `${Math.ceil(data.km_to_next)} km do balíčku` : "…"}</span>
              <span>{data ? `${data.km_per_pack} km = 1 balíček` : ""}</span>
            </div>
          </div>

          <div className="lk-stage">
            {view === "pack" && (
              <div className="lk-view">
                <div className="lk-packzone">
                  {isLoading ? <IonSpinner name="crescent" /> : (data?.packs_available ?? 0) > 0 ? (
                    <>
                      <svg
                        className={`lk-pack${bursting ? " burst" : " wiggle"}`}
                        viewBox="0 0 200 270"
                        onClick={() => void startOpen()}
                        role="button"
                        aria-label="Otevřít balíček"
                      >
                        <PackArt />
                      </svg>
                      <div className="lk-hint">Ťukni na balíček a otevři ho</div>
                    </>
                  ) : (
                    <div className="lk-nopack">
                      Žádný balíček. Najeď dalších{" "}
                      <b style={{ color: "var(--brass)" }}>{data ? `${Math.ceil(data.km_to_next)} km` : "…"}</b>{" "}
                      a další je tvůj.
                    </div>
                  )}
                  <div className="lk-odds">
                    <i className="c">● obyčejná 70 %</i><i className="r">● vzácná 20 %</i>
                    <i className="e">● epická 8 %</i><i className="l">● legendární 2 %</i>
                  </div>
                </div>
              </div>
            )}

            {view === "album" && (
              <div className="lk-view">
                <div className="lk-albhead">
                  <b>{owned}/{set.length}</b><span>samolepek nasbíráno</span>
                </div>
                <div className="lk-alb">
                  {set.map((c) => {
                    const n = inv[String(c.id)] ?? 0;
                    const R = RAR[c.rarity];
                    if (!n) {
                      return (
                        <div key={c.id} className={`lk-slot miss ${c.rarity}`}>
                          <span className="q">?</span><em>{c.nm}</em>
                        </div>
                      );
                    }
                    return (
                      <button key={c.id} className={`lk-slot ${c.rarity}`}
                        style={{ "--lk-rar": R.tok } as React.CSSProperties}
                        onClick={() => setDetail(c)}>
                        {n > 1 && <span className="cnt">×{n}</span>}
                        <img src={`${IMG_BASE}${c.img}`} alt={c.nm} />
                        <em>{c.nm}</em>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {view === "lb" && (
              <div className="lk-view">
                <div className="lk-lb">
                  {(data?.leaderboard ?? []).map((p, i) => (
                    <div key={`${p.name}-${i}`} className={`lk-lbrow${p.me ? " me" : ""}`}>
                      <span className="pos">{i + 1}</span>
                      <span className="who"><b>{p.me ? `${p.name} (ty)` : p.name}</b></span>
                      <span className="sc"><b>{p.col}/{set.length}</b><span>samolepek</span></span>
                    </div>
                  ))}
                  <div className="lk-lbnote">
                    Pořadí podle nasbíraných samolepek.<br />
                    Kilometry se počítají z Commanderu — žádné ruční zadávání.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lk-tabs">
            <button className={`lk-tab${view === "pack" ? " on" : ""}`} onClick={() => setView("pack")}>
              <span className="bdg">
                <svg viewBox="0 0 24 24"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" /><path d="M4 7.5l8 4.5 8-4.5M12 12v9" /></svg>
                {(data?.packs_available ?? 0) > 0 && <i>{data!.packs_available}</i>}
              </span>
              Balíček
            </button>
            <button className={`lk-tab${view === "album" ? " on" : ""}`} onClick={() => setView("album")}>
              <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 4v16" /></svg>
              Album
            </button>
            <button className={`lk-tab${view === "lb" ? " on" : ""}`} onClick={() => setView("lb")}>
              <svg viewBox="0 0 24 24"><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z" /><path d="M7 6H4a3 3 0 0 0 3 5M17 6h3a3 3 0 0 1-3 5" /></svg>
              Žebříček
            </button>
          </div>

          {/* ── reveal overlay ── */}
          {opening && cur && (
            <div ref={revealRef} className="lk-reveal" onClick={revealTap}>
              <div ref={wrapRef} className="lk-cardwrap">
                <div ref={raysRef} className="lk-rays" />
                <div
                  ref={card3dRef}
                  className={`lk-card3d${opening.phase === "spin" ? " spin" : ""}${opening.phase === "front" ? " flip" : ""}`}
                  onAnimationEnd={onSpinEnd}
                >
                  <div className="lk-face lk-back">
                    <div className="mk"><LamaMark size={74} /><div>LAMA KLUB</div></div>
                  </div>
                  <div
                    className="lk-face lk-front"
                    style={{ "--lk-rar": RAR[cur.rarity].tok, "--lk-glow": RAR[cur.rarity].glow } as React.CSSProperties}
                  >
                    {opening.phase === "front" && (
                      <>
                        <div className="art"><img src={`${IMG_BASE}${cur.img}`} alt={cur.nm} /></div>
                        <div className="nm"><b>{cur.nm}</b><span className="num">#{String(cur.id).padStart(2, "0")}</span></div>
                        <div className="lk-rar-row">
                          <span className="lk-chip">{RAR[cur.rarity].nm}</span>
                          <span className={`lk-dupe${cur.new ? " new" : ""}`}>{cur.new ? "NOVÁ!" : "duplikát"}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div ref={ring1Ref} className="lk-fxring" />
                <div ref={ring2Ref} className="lk-fxring b" />
              </div>
              <div ref={flashRef} className="lk-flash" />
              <div ref={splashRef} className="lk-splash" />
              <div className="lk-step">{opening.idx + 1} / {opening.cards.length}</div>
              <div className="lk-rhint">
                {opening.phase === "back" ? "ťukni pro otočení" : opening.phase === "front" ? "ťukni pro další" : "…"}
              </div>
              <div className="lk-minis">
                {opening.cards.map((c, i) => (
                  <div key={i} className={`lk-mini${i < opening.idx || (i === opening.idx && opening.phase === "front") ? ` ${c.rarity}` : ""}`} />
                ))}
              </div>
            </div>
          )}

          {/* ── souhrn ── */}
          {sumCards && (
            <div className="lk-sum">
              <h2>NOVÉ SAMOLEPKY</h2>
              <div className="lk-sumgrid">
                {sumCards.map((c, i) => (
                  <div key={i} className="lk-sumc"
                    style={{ "--lk-rar": RAR[c.rarity].tok, animationDelay: `${i * 90}ms` } as React.CSSProperties}>
                    <img src={`${IMG_BASE}${c.img}`} alt={c.nm} />
                    <div>{c.nm}</div>
                    <small className={c.new ? "new" : ""}>{c.new ? "NOVÁ" : RAR[c.rarity].nm.toLowerCase()}</small>
                  </div>
                ))}
              </div>
              <button className="lk-tab on" style={{ flex: "none", padding: "12px 26px" }}
                onClick={() => { setSumCards(null); setView("album"); }}>
                Do alba
              </button>
            </div>
          )}

          {/* ── detail samolepky ── */}
          {detail && (
            <div className="lk-modal" onClick={() => setDetail(null)}>
              <div className="lk-mcard"
                style={{ "--lk-rar": RAR[detail.rarity].tok, "--lk-glow": RAR[detail.rarity].glow } as React.CSSProperties}
                onClick={(e) => e.stopPropagation()}>
                <div className="art"><img src={`${IMG_BASE}${detail.img}`} alt={detail.nm} /></div>
                <h3>{detail.nm} <small>#{String(detail.id).padStart(2, "0")} · ×{inv[String(detail.id)] ?? 0}</small></h3>
                {detail.hlaska && <p>„{detail.hlaska}“</p>}
                <div className="lk-rar-row"><span className="lk-chip">{RAR[detail.rarity].nm}</span></div>
              </div>
            </div>
          )}

          <canvas ref={fxRef} className="lk-fx" />
        </div>
      </IonContent>
    </IonPage>
  );
}

/** Booster balíček — krimpované okraje, emblém s lamou, band BOOSTER. */
function PackArt() {
  const crimp = (y: number, dir: number) => {
    let d = `M22 ${y}`;
    for (let x = 22; x < 178; x += 13) d += ` l6.5 ${dir * 9} l6.5 ${-dir * 9}`;
    return d;
  };
  return (
    <>
      <defs>
        <linearGradient id="lkfoil" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2A3860" /><stop offset=".5" stopColor="#1B2440" /><stop offset="1" stopColor="#131A2D" />
        </linearGradient>
        <linearGradient id="lkband" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#F3BD57" /><stop offset="1" stopColor="#C7913B" />
        </linearGradient>
        <radialGradient id="lkbadge" cx=".5" cy=".42" r=".65">
          <stop offset="0" stopColor="#22335C" /><stop offset="1" stopColor="#131A2D" />
        </radialGradient>
      </defs>
      <rect x="22" y="24" width="156" height="222" fill="url(#lkfoil)" stroke="#38466E" strokeWidth="2" />
      <path d={crimp(24, -1)} fill="none" stroke="#38466E" strokeWidth="2" />
      <path d={crimp(246, 1)} fill="none" stroke="#38466E" strokeWidth="2" />
      <path d={`${crimp(24, -1)} V24 Z`} fill="#1B2440" />
      <path d={`${crimp(246, 1)} V246 Z`} fill="#1B2440" />
      <rect x="22" y="24" width="46" height="222" fill="rgba(255,255,255,.04)" />
      <text x="100" y="58" textAnchor="middle" fontFamily="Georgia,serif" fontSize="14" fill="#B9C4DD">R O U T E R A</text>
      <circle cx="100" cy="128" r="47" fill="url(#lkbadge)" stroke="url(#lkband)" strokeWidth="3" />
      <circle cx="100" cy="128" r="40" fill="none" stroke="rgba(227,177,92,.35)" strokeWidth="1" />
      <g transform="translate(70 99)">
        <g transform="scale(.5)">
          <ellipse cx="60" cy="112" rx="34" ry="5" fill="rgba(0,0,0,.25)" />
          <rect x="52" y="88" width="9" height="24" rx="4.5" fill="#E3B15C" />
          <rect x="88" y="88" width="9" height="24" rx="4.5" fill="#E3B15C" />
          <rect x="64" y="90" width="9" height="22" rx="4.5" fill="#C7913B" />
          <rect x="76" y="90" width="9" height="22" rx="4.5" fill="#C7913B" />
          <ellipse cx="74" cy="74" rx="32" ry="24" fill="#E3B15C" />
          <circle cx="103" cy="66" r="7" fill="#E3B15C" />
          <rect x="38" y="26" width="20" height="44" rx="10" fill="#E3B15C" />
          <path d="M34 16 q-2 -12 6 -10 q3 6 -1 11 z" fill="#E3B15C" />
          <path d="M46 16 q2 -12 10 -9 q2 6 -3 10 z" fill="#E3B15C" />
          <ellipse cx="41" cy="28" rx="15" ry="12" fill="#E3B15C" />
          <ellipse cx="29" cy="32" rx="8" ry="6.2" fill="#F3D9A4" />
          <circle cx="45" cy="26" r="2.4" fill="#141C31" />
          <circle cx="47" cy="25" r=".8" fill="#fff" />
          <path d="M34 37 q3 2.5 6 0" stroke="#141C31" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </g>
      </g>
      <rect x="22" y="192" width="156" height="30" fill="url(#lkband)" />
      <text x="100" y="213" textAnchor="middle" fontFamily="Georgia,serif" fontSize="16" fontWeight="bold" fill="#241A06">B O O S T E R</text>
      <text x="100" y="238" textAnchor="middle" fontFamily="ui-monospace,monospace" fontSize="9.5" fill="#7484A6">2 SAMOLEPKY</text>
    </>
  );
}
